import React, { useState, useMemo, useEffect, useRef } from 'react';
import { exportTasksToExcel } from '../utils/taskExport';

function ExcelView({ 
  tasks, 
  contractors, 
  events,
  user, 
  userRole, 
  onNavigate, 
  currentProject,
  projects,
  onProjectChange,
  setTasks 
}) {
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState('all');
  const [selectedTrade, setSelectedTrade] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [localNotes, setLocalNotes] = useState({}); // Local state for notes
  const notesTimeouts = useRef({}); // Debounce timeouts

  const isAdmin = userRole === 'admin';

  // Load calendar events from Firebase
  useEffect(() => {
    const loadCalendarEvents = async () => {
      if (!currentProject?.id) {
        setCalendarEvents([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        
        const eventsRef = collection(db, 'calendar_events');
        const q = query(
          eventsRef,
          where('project_id', '==', currentProject.id)
        );
        
        const snapshot = await getDocs(q);
        const events = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setCalendarEvents(events);
        setLoading(false);
      } catch (error) {
        console.error('Error loading calendar events:', error);
        setLoading(false);
      }
    };

    loadCalendarEvents();
  }, [currentProject?.id]);

  // Merge tasks with their calendar event data
  const mergedData = useMemo(() => {
    return tasks.map(task => {
      const taskEvents = calendarEvents.filter(e => e.task_id === task.id);
      const event = taskEvents.length > 0 ? taskEvents[0] : null;
      
      const contractor = event?.contractor_id 
        ? contractors.find(c => c.id === event.contractor_id)
        : task.contractor
        ? contractors.find(c => c.id === task.contractor)
        : null;
      
      return {
        id: task.id,
        task_id: task.id,
        event_id: event?.id || null,
        title: task.title,
        phase: task.phase || 'Other',
        trade: task.trade || contractor?.trade || 'Other',
        contractor_id: event?.contractor_id || task.contractor || null,
        contractor: contractor,
        start: event?.start || null,
        end: event?.end || null,
        completed: task.completed || false,
        completed_date: task.date_completed || event?.completed_date || null,
        notes: localNotes[task.id] !== undefined ? localNotes[task.id] : (event?.notes || task.notes || ''),
        order: task.order || 0,
        phase_order: task.phase_order || 0,
        hasEvent: !!event
      };
    });
  }, [tasks, calendarEvents, contractors, localNotes]);

  // Get unique phases and trades
  const phases = useMemo(() => {
    const phaseSet = new Set(mergedData.map(item => item.phase));
    return ['all', ...Array.from(phaseSet)];
  }, [mergedData]);

  const trades = useMemo(() => {
    const tradeSet = new Set(mergedData.map(item => item.trade));
    return ['all', ...Array.from(tradeSet)];
  }, [mergedData]);

  // Group and filter merged data by phase
  const groupedData = useMemo(() => {
    let filteredData = mergedData;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter(item => {
        const matchesTitle = item.title?.toLowerCase().includes(query);
        const matchesContractor = item.contractor?.name?.toLowerCase().includes(query);
        const matchesNotes = item.notes?.toLowerCase().includes(query);
        return matchesTitle || matchesContractor || matchesNotes;
      });
    }

    if (selectedPhase !== 'all') {
      filteredData = filteredData.filter(item => item.phase === selectedPhase);
    }

    if (selectedTrade !== 'all') {
      filteredData = filteredData.filter(item => item.trade === selectedTrade);
    }

    const grouped = {};
    filteredData.forEach(item => {
      const phase = item.phase;
      if (!grouped[phase]) {
        grouped[phase] = [];
      }
      grouped[phase].push(item);
    });

    const phaseOrder = [
      'Design & Planning',
      'Permits & Approvals',
      'Site Preparation',
      'Foundation & Utilities Setup',
      'Framing',
      'Rough-Ins',
      'Insulation & Drywall',
      'Interior Finishes',
      'Exterior Finishes',
      'Final Systems',
      'Final Exterior & Cleanup',
      'Site Services & Deliveries',
      'Other'
    ];

    const sortedGrouped = {};
    phases.forEach(phase => {
      if (grouped[phase]) {
        sortedGrouped[phase] = grouped[phase].sort((a, b) => {
          if (a.phase_order !== b.phase_order) {
            return a.phase_order - b.phase_order;
          }
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          if (a.start && b.start) {
            const dateA = a.start.seconds ? new Date(a.start.seconds * 1000) : new Date(a.start);
            const dateB = b.start.seconds ? new Date(b.start.seconds * 1000) : new Date(b.start);
            return dateA - dateB;
          }
          if (a.start && !b.start) return -1;
          if (!a.start && b.start) return 1;
          return 0;
        });
      }
    });

    Object.keys(grouped).forEach(phase => {
      if (!sortedGrouped[phase]) {
        sortedGrouped[phase] = grouped[phase].sort((a, b) => a.order - b.order);
      }
    });

    return sortedGrouped;
  }, [mergedData, selectedPhase, selectedTrade, searchQuery, phases]);

  const handleUpdate = async (item, updates) => {
    if (!isAdmin) return;

    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      // Only update tasks collection
      await updateDoc(doc(db, 'tasks', item.task_id), updates);
    } catch (error) {
      console.error('Error updating:', error);
    }
  };

  const handleContractorChange = async (item, contractorId) => {
    const contractor = contractors.find(c => c.id === contractorId);
    
    // Update tasks collection with contractor field
    const taskUpdates = { 
      contractor: contractorId || null
    };
    
    await handleUpdate(item, taskUpdates);
    
    // Update calendar_events collection with contractor_id, title, and colors
    if (item.event_id) {
      const newTitle = contractorId && contractor 
        ? `${item.title} (${contractor.name})`
        : item.title;
      
      try {
        const { doc, updateDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        
        await updateDoc(doc(db, 'calendar_events', item.event_id), {
          contractor_id: contractorId || null,
          title: newTitle,
          backgroundColor: contractorId ? '#ffc107' : '#4285f4',
          borderColor: contractorId ? '#ffc107' : '#4285f4'
        });
        
        // Update local state
        setCalendarEvents(prev => 
          prev.map(e => 
            e.id === item.event_id 
              ? { 
                  ...e, 
                  contractor_id: contractorId, 
                  title: newTitle,
                  backgroundColor: contractorId ? '#ffc107' : '#4285f4',
                  borderColor: contractorId ? '#ffc107' : '#4285f4'
                }
              : e
          )
        );
      } catch (error) {
        console.error('Error updating event:', error);
      }
    }
  };

  const handleDateChange = async (item, field, value) => {
    const date = value ? new Date(value) : null;
    if (date) {
      const offset = date.getTimezoneOffset();
      date.setMinutes(date.getMinutes() + offset);
      date.setHours(12, 0, 0, 0);
    }
    
    if ((field === 'start' || field === 'end') && date) {
      // For start/end dates, only update calendar_events, not tasks
      const calendarDate = field === 'end' ? new Date(date.getTime() + 86400000) : date;
      
      if (item.event_id) {
        try {
          const { doc, updateDoc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../firebase');
          await updateDoc(doc(db, 'calendar_events', item.event_id), { [field]: calendarDate });
          
          const eventDoc = await getDoc(doc(db, 'calendar_events', item.event_id));
          if (eventDoc.exists()) {
            setCalendarEvents(prev => 
              prev.map(e => e.id === item.event_id ? { id: eventDoc.id, ...eventDoc.data() } : e)
            );
          }
        } catch (error) {
          console.error('Error updating event date:', error);
        }
      } else {
        try {
          const { collection, addDoc } = await import('firebase/firestore');
          const { db } = await import('../firebase');
          
          const contractor = item.contractor_id ? contractors.find(c => c.id === item.contractor_id) : null;
          const newEvent = {
            task_id: item.task_id,
            title: contractor ? `${item.title} (${contractor.name})` : item.title,
            start: field === 'start' ? date : null,
            end: field === 'end' ? calendarDate : null,
            allDay: true,
            contractor_id: item.contractor_id || null,
            project_id: currentProject.id,
            backgroundColor: item.contractor_id ? '#ffc107' : '#4285f4',
            borderColor: item.contractor_id ? '#ffc107' : '#4285f4',
            created_at: new Date()
          };
          
          const docRef = await addDoc(collection(db, 'calendar_events'), newEvent);
          setCalendarEvents(prev => [...prev, { id: docRef.id, ...newEvent }]);
        } catch (error) {
          console.error('Error creating event:', error);
        }
      }
    } else if (field === 'completed_date') {
      // For completed_date, only update tasks collection
      await handleUpdate(item, { 
        date_completed: date,
        completed: !!date
      });
    }
  };

  const handleCompletedChange = async (item, checked) => {
    const date = checked ? new Date() : null;
    
    // Update local calendar events immediately (just for UI color changes)
    setCalendarEvents(prev => 
      prev.map(e => 
        e.id === item.event_id 
          ? { ...e, completed: checked }
          : e
      )
    );
    
    // Update tasks state immediately for color changes
    if (setTasks) {
      setTasks(prev => 
        prev.map(t => 
          t.id === item.task_id
            ? { ...t, completed: checked, date_completed: date }
            : t
        )
      );
    }
    
    // Update database - only tasks collection gets date_completed
    await handleUpdate(item, {
      completed: checked,
      date_completed: date
    });
  };

  const handleNotesChange = (item, notes) => {
    // Update local state immediately for fast UI response
    setLocalNotes(prev => ({ ...prev, [item.task_id]: notes }));
    
    // Clear existing timeout
    if (notesTimeouts.current[item.task_id]) {
      clearTimeout(notesTimeouts.current[item.task_id]);
    }
    
    // Set new timeout to save after 500ms of no typing
    notesTimeouts.current[item.task_id] = setTimeout(async () => {
      // Update tasks collection
      await handleUpdate(item, { notes });
      
      // Also update calendar_events if event exists
      if (item.event_id) {
        try {
          const { doc, updateDoc } = await import('firebase/firestore');
          const { db } = await import('../firebase');
          await updateDoc(doc(db, 'calendar_events', item.event_id), { notes });
          
          // Update local calendar events state
          setCalendarEvents(prev => 
            prev.map(e => e.id === item.event_id ? { ...e, notes } : e)
          );
        } catch (error) {
          console.error('Error updating event notes:', error);
        }
      }
    }, 500);
  };

  const formatDateForInput = (date, isEndDate = false) => {
    if (!date) return '';
    
    if (date.seconds) {
      const d = new Date(date.seconds * 1000);
      if (isEndDate) {
        d.setDate(d.getDate() - 1);
      }
      return d.toISOString().split('T')[0];
    }
    
    const d = date instanceof Date ? date : new Date(date);
    if (isEndDate) {
      d.setDate(d.getDate() - 1);
    }
    return d.toISOString().split('T')[0];
  };

  return (
    <>
      <div 
        className={`mobile-nav-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      <div className={`mobile-nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-nav-header">
          <h3 style={{ margin: 0 }}>Menu</h3>
          <button 
            className="mobile-nav-close"
            onClick={() => setMobileMenuOpen(false)}
          >
            ×
          </button>
        </div>
        <ul className="mobile-nav-links">
          <li>
            <button onClick={() => { onNavigate('calendar'); setMobileMenuOpen(false); }}>
              <span>📅</span> Calendar
            </button>
          </li>
          {isAdmin && (
            <>
              <li>
                <button onClick={() => { onNavigate('punchlist'); setMobileMenuOpen(false); }}>
                  <span>📋</span> Punch List
                </button>
              </li>
              <li>
                <button onClick={() => { onNavigate('swornstatements'); setMobileMenuOpen(false); }}>
                  <span>📊</span> Sworn Statements
                </button>
              </li>
            </>
          )}
          <li>
            <button onClick={() => { onNavigate('selections'); setMobileMenuOpen(false); }}>
              <span>🏠</span> Selections
            </button>
          </li>
          <li>
            <button className="active" onClick={() => setMobileMenuOpen(false)}>
              <span>📋</span> Line View
            </button>
          </li>
          <li>
            <button onClick={() => { onNavigate('files'); setMobileMenuOpen(false); }}>
                <span>📁</span> Files
            </button>
          </li>
        </ul>
      </div>

      <div className="excel-view-container">
        <div className="mobile-header">
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(true)}
          >
            ☰
          </button>
          <select
            className="mobile-project-selector"
            value={currentProject?.id || ''}
            onChange={(e) => {
              if (onProjectChange && e.target.value) {
                onProjectChange(e.target.value);
              }
            }}
          >
            <option value="">Select Project</option>
            {projects && projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        <div className="excel-header">
          <div className="excel-controls">
            <h1>📋 Line View </h1>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              flex: '1',
              maxWidth: '400px'
            }}>
              <input
                type="text"
                placeholder="🔍 Search tasks, contractors, or notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none'
                }}
                className="search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            <button 
              onClick={() => onNavigate('calendar')}
              className="excel-btn"
              style={{ backgroundColor: '#b708e7ff', marginLeft: '20px'}}
            >
              Calendar
            </button>
            {isAdmin && (
              <>
                <button 
                  onClick={() => onNavigate('punchlist')}
                  className="excel-btn"
                  style={{ backgroundColor: '#ffc107' }}
                >
                  Punch List
                </button>
                <button 
                  onClick={() => onNavigate('swornstatements')}
                  className="excel-btn"
                  style={{ backgroundColor: '#17a2b8' }}
                >
                  Sworn Statements
                </button>
              </>
            )}
            <button 
                onClick={() => onNavigate('selections')} 
                className="excel-btn" 
                style={{ backgroundColor: '#007bff', borderColor: '#007bff' }}
            >
                Selections
            </button>
            <button 
                onClick={() => onNavigate('files')}
                className="excel-btn"
                style={{ padding: '8px 16px', marginLeft: '10px', backgroundColor: '#d78c3d', borderColor: '#d78c3d' }}
            >
                📁 Files
            </button>
            <button
                onClick={() => exportTasksToExcel(tasks, currentProject?.name, events, contractors)}
                className="excel-btn"
                style={{ backgroundColor: '#28a745' }}
                disabled={!tasks.length}
            >
                🖨️ Print
            </button>

            {isAdmin && (
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem',
                    flex: '0 1 auto',
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '300px'
                }} className="project-selector-container">
                    <label htmlFor="project-select" style={{ 
                    fontSize: '14px', 
                    color: '#666',
                    display: 'none'
                    }} className="desktop-user-info">
                    Project:
                    </label>
                    <select
                    id="project-select"
                    value={currentProject?.id || ''}
                    onChange={(e) => {
                        const selectedId = e.target.value;
                        if (selectedId) {
                        onProjectChange(selectedId);
                        }
                    }}
                    style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #ddd',
                        fontSize: '14px',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        width: '100%',
                        fontWeight: '500',
                        color: '#333',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                    >
                    {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                        {project.name}
                        </option>
                    ))}
                    </select>
                </div>
                )}
            </div>
        </div>

        <div className="excel-content">
          {loading ? (
            <div className="loading-state">
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
              <div>Loading schedule...</div>
            </div>
          ) : (
            <table className="excel-table">
              <thead>
                <tr>
                  <th className="checkbox-cell">Complete</th>
                  <th className="task-cell">Task</th>
                  <th className="contractor-cell">Contractor</th>
                  <th className="date-cell">Scheduled Start Date</th>
                  <th className="date-cell">Scheduled End Date</th>
                  <th className="date-cell">Completed Date</th>
                  <th className="notes-cell">Notes</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedData).length > 0 ? (
                  Object.entries(groupedData).map(([phase, phaseItems]) => (
                    <React.Fragment key={phase}>
                      {phaseItems.map(item => {
                        const isCompleted = item.completed || item.completed_date;
                        const isUnscheduled = !item.start && !item.end;
                        
                        return (
                          <tr 
                            key={item.id} 
                            className={isCompleted ? 'completed-row' : isUnscheduled ? 'unscheduled-row' : ''}
                          >
                            <td className="checkbox-cell">
                              <input
                                type="checkbox"
                                checked={isCompleted || false}
                                onChange={(e) => handleCompletedChange(item, e.target.checked)}
                                disabled={!isAdmin}
                              />
                            </td>
                            <td className="task-cell">
                              {item.title}
                            </td>
                            <td className="contractor-cell">
                              <select
                                value={item.contractor_id || ''}
                                onChange={(e) => handleContractorChange(item, e.target.value)}
                                disabled={!isAdmin}
                              >
                                <option value="">Select Contractor</option>
                                {contractors.map(contractor => (
                                  <option key={contractor.id} value={contractor.id}>
                                    {contractor.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="date-cell">
                              <input
                                type="date"
                                value={formatDateForInput(item.start)}
                                onChange={(e) => handleDateChange(item, 'start', e.target.value)}
                                disabled={!isAdmin}
                              />
                            </td>
                            <td className="date-cell">
                              <input
                                type="date"
                                value={formatDateForInput(item.end, true)}
                                onChange={(e) => handleDateChange(item, 'end', e.target.value)}
                                disabled={!isAdmin}
                              />
                            </td>
                            <td className="date-cell">
                              <input
                                type="date"
                                value={formatDateForInput(item.completed_date)}
                                onChange={(e) => handleDateChange(item, 'completed_date', e.target.value)}
                                disabled={!isAdmin}
                              />
                            </td>
                            <td className="notes-cell">
                              <textarea
                                value={item.notes || ''}
                                onChange={(e) => handleNotesChange(item, e.target.value)}
                                placeholder="Add notes..."
                                disabled={!isAdmin}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7">
                      <div className="empty-state">
                        <div className="empty-state-icon">📋</div>
                        <div className="empty-state-text">
                          {searchQuery.trim() 
                            ? `No tasks found matching "${searchQuery}"`
                            : selectedPhase !== 'all' || selectedTrade !== 'all' 
                            ? 'No tasks found for the selected filters.' 
                            : 'No tasks available for this project.'}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

export default ExcelView;